import { CellType, PowerUpType, type LevelConfig } from '../types';
import { MAP_COLS, MAP_ROWS, SAFE_ZONE_RADIUS } from '../constants';

export type Grid = CellType[][];

export interface GeneratedLevel {
  grid: Grid;
  playerStarts: Array<{ col: number; row: number }>;
  enemyStarts: Array<{ col: number; row: number }>;
  exitCol: number;
  exitRow: number;
  powerups: Array<{ col: number; row: number; type: PowerUpType }>;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function generateLevel(config: LevelConfig): GeneratedLevel {
  const rng = seededRandom(config.seed);
  const grid: Grid = [];

  // Build base grid with indestructible walls in classic pattern
  for (let row = 0; row < MAP_ROWS; row++) {
    grid[row] = [];
    for (let col = 0; col < MAP_COLS; col++) {
      if (row === 0 || row === MAP_ROWS - 1 || col === 0 || col === MAP_COLS - 1) {
        grid[row][col] = CellType.WALL;
      } else if (row % 2 === 0 && col % 2 === 0) {
        grid[row][col] = CellType.WALL;
      } else {
        grid[row][col] = CellType.FLOOR;
      }
    }
  }

  // Player starts (corners)
  const playerStarts = [
    { col: 1, row: 1 },
    { col: MAP_COLS - 2, row: MAP_ROWS - 2 },
    { col: 1, row: MAP_ROWS - 2 },
    { col: MAP_COLS - 2, row: 1 },
  ];

  // Mark safe zones around player starts
  const safeCells = new Set<string>();
  for (const p of playerStarts) {
    for (let dr = -SAFE_ZONE_RADIUS; dr <= SAFE_ZONE_RADIUS; dr++) {
      for (let dc = -SAFE_ZONE_RADIUS; dc <= SAFE_ZONE_RADIUS; dc++) {
        if (Math.abs(dr) + Math.abs(dc) <= SAFE_ZONE_RADIUS) {
          safeCells.add(`${p.col + dc},${p.row + dr}`);
        }
      }
    }
  }

  // Collect placeable floor cells
  const floorCells: Array<{ col: number; row: number }> = [];
  for (let row = 1; row < MAP_ROWS - 1; row++) {
    for (let col = 1; col < MAP_COLS - 1; col++) {
      if (grid[row][col] === CellType.FLOOR && !safeCells.has(`${col},${row}`)) {
        floorCells.push({ col, row });
      }
    }
  }

  const shuffledFloor = shuffle(floorCells, rng);
  const blockCount = Math.floor(shuffledFloor.length * config.blockDensity);
  const blocks: Array<{ col: number; row: number }> = [];

  for (let i = 0; i < blockCount && i < shuffledFloor.length; i++) {
    const { col, row } = shuffledFloor[i];
    grid[row][col] = CellType.BLOCK;
    blocks.push({ col, row });
  }

  // Choose exit location (behind a block)
  let exitCol = -1, exitRow = -1;
  const blockShuffled = shuffle(blocks, rng);
  for (const b of blockShuffled) {
    // Prefer blocks not adjacent to player start
    if (!safeCells.has(`${b.col},${b.row}`)) {
      exitCol = b.col;
      exitRow = b.row;
      break;
    }
  }
  if (exitCol === -1 && blocks.length > 0) {
    exitCol = blocks[0].col;
    exitRow = blocks[0].row;
  }

  // Place power-ups in remaining block positions
  const powerupTypes = Object.values(PowerUpType);
  const powerups: Array<{ col: number; row: number; type: PowerUpType }> = [];
  for (const b of blockShuffled) {
    if (b.col === exitCol && b.row === exitRow) continue;
    if (rng() < config.powerupChance) {
      powerups.push({
        col: b.col,
        row: b.row,
        type: powerupTypes[Math.floor(rng() * powerupTypes.length)],
      });
    }
  }

  // Enemy spawn positions (away from player starts)
  // Filter cells that are still FLOOR after blocks have been placed
  const enemyFloor = shuffle(floorCells.filter(c =>
    !safeCells.has(`${c.col},${c.row}`) && grid[c.row][c.col] === CellType.FLOOR
  ), rng);
  const enemyStarts = enemyFloor.slice(0, config.enemyCount);

  return { grid, playerStarts, enemyStarts, exitCol, exitRow, powerups };
}

export function isPassable(grid: Grid, col: number, row: number, wallPass = false): boolean {
  if (col < 0 || row < 0 || col >= MAP_COLS || row >= MAP_ROWS) return false;
  const cell = grid[row][col];
  if (cell === CellType.WALL) return false;
  if (cell === CellType.BLOCK && !wallPass) return false;
  return true;
}

export function findPath(
  grid: Grid,
  startCol: number, startRow: number,
  endCol: number, endRow: number,
  bombMap?: Map<string, boolean>
): Array<{ col: number; row: number }> | null {
  type Node = { col: number; row: number; g: number; h: number; f: number; parent: Node | null };

  const key = (c: number, r: number) => `${c},${r}`;
  const heuristic = (c: number, r: number) => Math.abs(c - endCol) + Math.abs(r - endRow);

  const open = new Map<string, Node>();
  const closed = new Set<string>();

  const startNode: Node = { col: startCol, row: startRow, g: 0, h: heuristic(startCol, startRow), f: 0, parent: null };
  startNode.f = startNode.g + startNode.h;
  open.set(key(startCol, startRow), startNode);

  const dirs = [{ dc: 0, dr: -1 }, { dc: 0, dr: 1 }, { dc: -1, dr: 0 }, { dc: 1, dr: 0 }];

  while (open.size > 0) {
    let current: Node | null = null;
    for (const node of open.values()) {
      if (!current || node.f < current.f) current = node;
    }
    if (!current) break;

    if (current.col === endCol && current.row === endRow) {
      const path: Array<{ col: number; row: number }> = [];
      let n: Node | null = current;
      while (n) { path.unshift({ col: n.col, row: n.row }); n = n.parent; }
      return path;
    }

    open.delete(key(current.col, current.row));
    closed.add(key(current.col, current.row));

    for (const { dc, dr } of dirs) {
      const nc = current.col + dc;
      const nr = current.row + dr;
      const nk = key(nc, nr);
      if (closed.has(nk)) continue;
      if (!isPassable(grid, nc, nr)) continue;
      if (bombMap?.has(nk)) continue;

      const g = current.g + 1;
      const existing = open.get(nk);
      if (!existing || g < existing.g) {
        const node: Node = { col: nc, row: nr, g, h: heuristic(nc, nr), f: 0, parent: current };
        node.f = node.g + node.h;
        open.set(nk, node);
      }
    }

    if (open.size > 200) break; // safety limit
  }

  return null;
}
